import { useState } from 'react';
import { IconCoin } from '@tabler/icons-react';
import { useAuthStore } from '../../lib/auth.js';
import { ApiError } from '../../lib/api.js';
import { Button } from '../ui/Button.js';
import { Input } from '../ui/Input.js';
import { SendModalShell } from '../send/SendModalShell.js';
import { audToCents, centsToAudDisplay, centsToInput } from '../../lib/money.js';
import { buildFeeConfirm } from '../../lib/pricing.js';
import { editorLabel } from '../../lib/editor-label.js';
import { formatDate } from '../../lib/format.js';
import {
  useCourseFeeHistory,
  useSetCourseFee,
  type PricingCourseRow,
} from '../../hooks/usePricing.js';
import { ScholarshipSection } from './ScholarshipSection.js';

/** Local "are you sure?" dialog — mirrors the one in StaffDetailPanel (no shared component). */
function ConfirmDialog({
  open,
  title,
  body,
  confirmLabel,
  loading,
  onConfirm,
  onCancel,
}: {
  open: boolean;
  title: string;
  body: string;
  confirmLabel: string;
  loading: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}) {
  return (
    <SendModalShell open={open} onClose={onCancel} blocking={loading}>
      <div className="p-6">
        <h2 className="font-serif text-xl tracking-tight text-ink">{title}</h2>
        <p className="mt-2 text-sm text-muted">{body}</p>
        <div className="mt-6 flex justify-end gap-2">
          <Button variant="secondary" onClick={onCancel} disabled={loading}>
            Cancel
          </Button>
          <Button variant="primary" loading={loading} onClick={onConfirm}>
            {confirmLabel}
          </Button>
        </div>
      </div>
    </SendModalShell>
  );
}

/** Read-only reference tile for a college-published fee. */
function RefTile({ label, cents }: { label: string; cents: number | null }) {
  return (
    <div className="rounded-lg border border-line bg-cream/40 px-3 py-2">
      <div className="text-[11px] uppercase tracking-wide text-muted">{label}</div>
      <div className="text-sm font-medium text-ink">
        {cents != null ? centsToAudDisplay(cents) : '—'}
      </div>
    </div>
  );
}

export function PricingDetailPanel({ course }: { course: PricingCourseRow }) {
  const currentUserId = useAuthStore((s) => s.user?.id);
  const history = useCourseFeeHistory(course.courseId);
  const setFee = useSetCourseFee();

  const currentCents = course.agencyFee?.tuitionCents ?? null;
  const [value, setValue] = useState(() =>
    currentCents != null ? centsToInput(currentCents) : '',
  );
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [confirmOpen, setConfirmOpen] = useState(false);

  const parsedCents = audToCents(value);
  const valid = parsedCents !== null;
  const busy = setFee.isPending;

  // The currently-effective version = newest whose valid_from is on/before today
  // (history is newest-first). v1 has no future-dating, but this stays correct if
  // a future-dated version ever exists.
  const today = new Date().toLocaleDateString('en-CA'); // YYYY-MM-DD (local)
  const currentVersionId = history.data?.history.find((v) => v.validFrom <= today)?.id ?? null;

  async function commit() {
    if (parsedCents === null) return;
    setError(null);
    setSuccess(null);
    try {
      await setFee.mutateAsync({ masterCourseId: course.courseId, tuitionCents: parsedCents });
      setSuccess(`Fee set to ${centsToAudDisplay(parsedCents)}.`);
      setConfirmOpen(false);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Something went wrong. Please try again.');
    }
  }

  const showInputError = value.trim() !== '' && !valid;

  return (
    <div className="surface p-6">
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <h2 className="truncate font-serif text-xl tracking-tight text-ink">{course.name}</h2>
          <p className="truncate text-sm text-muted">{course.providerName}</p>
        </div>
        <IconCoin size={22} className="shrink-0 text-gold" />
      </div>

      {/* College-published fees — read-only reference */}
      <div className="mt-5 grid grid-cols-2 gap-3">
        <RefTile label="Published (annual)" cents={course.publishedAnnualTuitionCents} />
        <RefTile label="Published (total)" cents={course.publishedTotalProgramCents} />
      </div>

      {/* Current agency fee */}
      <div className="mt-4">
        <div className="text-[11px] uppercase tracking-wide text-muted">Current agency fee</div>
        <div className="text-lg font-medium text-navy">
          {currentCents != null ? centsToAudDisplay(currentCents) : 'Not set yet'}
        </div>
      </div>

      {/* Set fee */}
      <div className="mt-4">
        <label htmlFor="fee-input" className="mb-1 block text-sm text-ink">
          Set agency fee (AUD)
        </label>
        <div className="flex items-start gap-2">
          <div className="flex-1">
            <Input
              id="fee-input"
              inputMode="decimal"
              placeholder="0.00"
              value={value}
              onChange={(e) => {
                setValue(e.target.value);
                setError(null);
                setSuccess(null);
              }}
              invalid={showInputError}
            />
            {showInputError && (
              <p className="mt-1 text-xs text-danger">
                Enter a valid amount — numbers only, up to 2 decimal places.
              </p>
            )}
          </div>
          <Button onClick={() => setConfirmOpen(true)} disabled={!valid || busy}>
            Save
          </Button>
        </div>
      </div>

      {error && (
        <div className="mt-3 rounded-lg border border-danger/30 bg-danger-bg px-3 py-2 text-sm text-danger">
          {error}
        </div>
      )}
      {success && (
        <div className="mt-3 rounded-lg border border-success/30 bg-success-bg px-3 py-2 text-sm text-success">
          {success}
        </div>
      )}

      {/* Change history */}
      <div className="mt-6">
        <h3 className="mb-2 font-serif text-base text-ink">Change history</h3>
        {history.isLoading && <p className="text-sm text-muted">Loading history…</p>}
        {history.error && <p className="text-sm text-danger">Couldn't load the history.</p>}
        {!history.isLoading && !history.error && (history.data?.history.length ?? 0) === 0 && (
          <p className="text-sm text-muted">No fee has been set yet.</p>
        )}
        {!history.isLoading && !history.error && (history.data?.history.length ?? 0) > 0 && (
          <ul className="divide-y divide-line">
            {history.data!.history.map((v) => (
              <li key={v.id} className="py-2">
                <div className="text-sm text-ink">
                  {centsToAudDisplay(v.tuitionCents)}
                  {v.id === currentVersionId && (
                    <span className="badge ml-2 bg-success-bg text-success">Current</span>
                  )}
                </div>
                <div className="text-xs text-muted">
                  Effective {formatDate(v.validFrom)} · {editorLabel(v, currentUserId)} ·{' '}
                  {formatDate(v.createdAt)}
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>

      <ScholarshipSection course={course} />

      <ConfirmDialog
        open={confirmOpen}
        title="Set course fee"
        body={parsedCents !== null ? buildFeeConfirm(course.name, parsedCents, currentCents) : ''}
        confirmLabel="Set fee"
        loading={busy}
        onConfirm={() => void commit()}
        onCancel={() => setConfirmOpen(false)}
      />
    </div>
  );
}
